import React, { useState, useMemo, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CreateBountyModal from "../../Components/bounty/CreateBountyModal";
import BountyCompletionModal from "../../Components/bounty/BountyCompletionModal";
import { bountyApi } from "../../Components/bountyApi";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import { RoleContext } from "../../Contexts/RoleContext";
import { toast } from "react-toastify";
import styles from "./BountyBoard.module.scss";

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
};

function formatReward(b) {
  const type = b.reward_type || "Cash";
  const val = b.reward_value ?? 0;
  return `${type}: ${Number(val).toLocaleString()}`;
}

function formatExpires(expiresAt) {
  if (!expiresAt) return "—";
  const d = new Date(expiresAt);
  const now = new Date();
  if (d <= now) return "Expired";
  return d.toLocaleDateString(undefined, { dateStyle: "short" });
}

function getStatusChip(bounty) {
  const now = new Date();
  const expired = bounty.expires_at && new Date(bounty.expires_at) <= now && bounty.status !== "completed";
  if (expired) return <Chip size="small" label="Expired" color="default" variant="outlined" />;
  if (bounty.status === "completed") return <Chip size="small" label="Completed" color="success" variant="outlined" />;
  if (bounty.status === "claimed") return <Chip size="small" label="Claimed" color="primary" variant="outlined" />;
  return <Chip size="small" label="Available" color="info" variant="outlined" />;
}

export default function BountyBoardPage() {
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { role } = useContext(RoleContext);

  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [completedBounty, setCompletedBounty] = useState(null);

  const isManager = role === "admin" || role === "Manager";

  const fetchBounties = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await bountyApi.list();
      setBounties(data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load bounties.");
      setBounties([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBounties();
  }, [fetchBounties]);

  const handleCreateBounty = async (data) => {
    try {
      const res = await bountyApi.create({
        name: data.name,
        description: data.description || "",
        reward_type: data.reward_type || "Cash",
        reward_value: data.reward_value,
        conversions_required: data.conversions_required ?? 1,
        scope_type: data.scope_type || "overall",
        scope_id: data.scope_id ?? null,
        expires_at: data.expires_at || null,
      });
      const newBounty = res.bounty;
      if (newBounty) setBounties((prev) => [newBounty, ...prev]);
      toast.success("Bounty created.");
    } catch (err) {
      toast.error(err.message || "Failed to create bounty.");
    }
  };

  const handleClaim = async (bounty) => {
    try {
      const res = await bountyApi.claim(bounty.id);
      if (res.bounty) setBounties((prev) => prev.map((b) => (b.id === bounty.id ? res.bounty : b)));
      toast.success("Bounty claimed.");
    } catch (err) {
      toast.error(err.message || "Failed to claim bounty.");
    }
  };

  const handleComplete = async (bounty) => {
    try {
      const res = await bountyApi.complete(bounty.id);
      if (res.bounty) setBounties((prev) => prev.map((b) => (b.id === bounty.id ? res.bounty : b)));
      setCompletedBounty(res.bounty || bounty);
      setCompletionModalOpen(true);
      toast.success("Bounty completed.");
    } catch (err) {
      toast.error(err.message || "Failed to complete bounty.");
    }
  };

  const isExpired = (b) => b.expires_at && new Date(b.expires_at) <= new Date() && b.status !== "completed";
  const canClaim = (b) => b.status === "available" && !isExpired(b) && b.can_claim === true;
  const canComplete = (b) => b.status === "claimed" && (b.claimedBy?.isCurrentUser || isManager) && !isExpired(b);

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box flex={1}>
                    <Typography variant="h4" className={styles.pageTitle}>
                      Bounties
                    </Typography>
                    <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Conversion-based rewards: claim and complete for program, course, or overall.
                    </Typography>
                  </Box>
                  {isManager && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setCreateModalOpen(true)}
                    >
                      + Create Bounty
                    </Button>
                  )}
                </Stack>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : bounties.length === 0 ? (
                <Box className={styles.emptyState}>
                  <Typography color="text.secondary">No bounties yet.</Typography>
                  {isManager && (
                    <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setCreateModalOpen(true)}>
                      Create first bounty
                    </Button>
                  )}
                </Box>
              ) : (
                <TableContainer className={styles.tableWrap}>
                  <Table size="small" stickyHeader aria-label="Bounties list">
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.tableHead}>Name</TableCell>
                        <TableCell className={styles.tableHead}>Scope</TableCell>
                        <TableCell className={styles.tableHead}>Reward</TableCell>
                        <TableCell className={styles.tableHead}>Progress</TableCell>
                        <TableCell className={styles.tableHead}>Status</TableCell>
                        <TableCell className={styles.tableHead}>Expires</TableCell>
                        <TableCell className={styles.tableHead}>Claimed by</TableCell>
                        <TableCell className={styles.tableHead} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bounties.map((bounty) => (
                        <TableRow key={bounty.id} hover className={isExpired(bounty) ? styles.rowExpired : ""}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {bounty.name}
                            </Typography>
                            {bounty.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {bounty.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {bounty.scope_display || bounty.scope_type || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatReward(bounty)}</TableCell>
                          <TableCell>
                            {bounty.status === "available" && bounty.conversions_required != null && (
                              <Typography variant="body2">
                                {(bounty.current_user_conversion_count ?? 0)} / {bounty.conversions_required} conversions
                                {bounty.can_claim && (
                                  <Typography component="span" variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                                    · Can claim
                                  </Typography>
                                )}
                              </Typography>
                            )}
                            {(bounty.status === "claimed" || bounty.status === "completed") && bounty.conversions_required != null && (
                              <Typography variant="body2">
                                {(bounty.conversion_count ?? 0)} / {bounty.conversions_required} conversions
                              </Typography>
                            )}
                            {bounty.status === "available" && !bounty.conversions_required && "—"}
                          </TableCell>
                          <TableCell>{getStatusChip(bounty)}</TableCell>
                          <TableCell>{formatExpires(bounty.expires_at)}</TableCell>
                          <TableCell>
                            {bounty.claimedBy ? (
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar
                                  sx={{ width: 24, height: 24, fontSize: 12 }}
                                  src={bounty.claimedBy.avatar}
                                >
                                  {bounty.claimedBy.name?.charAt(0) || "?"}
                                </Avatar>
                                <Typography variant="body2">{bounty.claimedBy.name}</Typography>
                              </Stack>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                              {canClaim(bounty) && (
                                <Button size="small" variant="contained" onClick={() => handleClaim(bounty)}>
                                  Claim
                                </Button>
                              )}
                              {canComplete(bounty) && (
                                <Button size="small" variant="contained" color="success" onClick={() => handleComplete(bounty)}>
                                  Complete
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => navigate(`/bounties/${bounty.id}`, { state: { bounty } })}
                              >
                                View
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

        <CreateBountyModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSubmit={handleCreateBounty}
        />

        <BountyCompletionModal
          open={completionModalOpen}
          bounty={completedBounty}
          onClose={() => {
            setCompletionModalOpen(false);
            setCompletedBounty(null);
          }}
        />
      </Box>
    </ThemeProvider>
  );
}
